import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined';
import EmailIcon from '@mui/icons-material/EmailOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import LinkIcon from '@mui/icons-material/LinkOutlined';
import {
  getPortalInviteHistory,
  getPortalInviteLink,
  sendPortalInviteEmail,
  sendPortalInviteWhatsApp,
} from '../../api/students';
import { PortalInviteChannel, PortalInviteRecord, PortalInviteStatus } from '../../types/portal';

const STATUS_COLOR: Record<PortalInviteStatus, 'success' | 'warning' | 'error'> = {
  SENT: 'success',
  PENDING: 'warning',
  FAILED: 'error',
};

const CHANNEL_LABEL: Record<PortalInviteChannel, string> = {
  EMAIL: 'Email',
  WHATSAPP: 'WhatsApp',
  MANUAL: 'Manual link',
};

/** Latest attempt per channel, for the at-a-glance status summary — the
 * full history table below still shows every attempt ever made. */
const latestByChannel = (history: PortalInviteRecord[], channel: PortalInviteChannel) =>
  history.find((h) => h.channel === channel) ?? null;

interface PortalInvitationDialogProps {
  open: boolean;
  studentId: number;
  studentHasEmail: boolean;
  onClose: () => void;
}

/** Student Portal invitation system (Phase 68) — Email / WhatsApp / manual
 * link, each recorded as an append-only StudentPortalInvite row rather than
 * a single mutable status, so the history table below is a true audit trail.
 * "Copy portal link" always mints a fresh token and invalidates any prior
 * unused one (see studentPortal.service.ts's getPortalInviteLink) since raw
 * tokens are never stored/recoverable — there is no separate "regenerate"
 * action because there is nothing else it could safely mean. */
export const PortalInvitationDialog = ({ open, studentId, studentHasEmail, onClose }: PortalInvitationDialogProps) => {
  const queryClient = useQueryClient();
  const [copiedLink, setCopiedLink] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const historyQuery = useQuery({
    queryKey: ['portal-invite-history', studentId],
    queryFn: () => getPortalInviteHistory(studentId),
    enabled: open,
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['portal-invite-history', studentId] });
    queryClient.invalidateQueries({ queryKey: ['student', studentId] });
  };

  const errorMessageFrom = (err: unknown, fallback: string) =>
    isAxiosError(err) ? (err.response?.data?.message ?? fallback) : fallback;

  const emailMutation = useMutation({
    mutationFn: () => sendPortalInviteEmail(studentId),
    onSuccess: () => {
      setActionError(null);
      invalidate();
    },
    onError: (err) => setActionError(errorMessageFrom(err, 'Failed to send email invite')),
  });

  const whatsAppMutation = useMutation({
    mutationFn: () => sendPortalInviteWhatsApp(studentId),
    onSuccess: () => {
      setActionError(null);
      invalidate();
    },
    onError: (err) => setActionError(errorMessageFrom(err, 'Failed to send WhatsApp invite')),
  });

  const linkMutation = useMutation({
    mutationFn: () => getPortalInviteLink(studentId),
    onSuccess: async (result) => {
      setActionError(null);
      invalidate();
      try {
        await navigator.clipboard.writeText(result.link);
        setCopiedLink(result.link);
      } catch {
        setCopiedLink(result.link);
      }
    },
    onError: (err) => setActionError(errorMessageFrom(err, 'Failed to generate portal link')),
  });

  const history = historyQuery.data ?? [];
  const latestEmail = latestByChannel(history, 'EMAIL');
  const latestWhatsApp = latestByChannel(history, 'WHATSAPP');

  const handleClose = () => {
    setCopiedLink(null);
    setActionError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Student portal access</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {actionError && (
            <Alert severity="error" onClose={() => setActionError(null)}>
              {actionError}
            </Alert>
          )}

          {!studentHasEmail && (
            <Alert severity="warning">
              This student has no email address on file. Add one before sending an invite, or use
              &ldquo;Copy portal link&rdquo; once an account exists.
            </Alert>
          )}

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Chip
              size="small"
              icon={<EmailIcon fontSize="small" />}
              label={`Email: ${latestEmail ? latestEmail.status : 'Not sent'}`}
              color={latestEmail ? STATUS_COLOR[latestEmail.status] : 'default'}
            />
            <Chip
              size="small"
              icon={<WhatsAppIcon fontSize="small" />}
              label={`WhatsApp: ${latestWhatsApp ? latestWhatsApp.status : 'Not sent'}`}
              color={latestWhatsApp ? STATUS_COLOR[latestWhatsApp.status] : 'default'}
            />
          </Stack>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              size="small"
              variant="outlined"
              startIcon={<EmailIcon />}
              disabled={emailMutation.isPending || !studentHasEmail}
              onClick={() => emailMutation.mutate()}
            >
              {emailMutation.isPending ? 'Sending…' : 'Send via email'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<WhatsAppIcon />}
              disabled={whatsAppMutation.isPending}
              onClick={() => whatsAppMutation.mutate()}
            >
              {whatsAppMutation.isPending ? 'Sending…' : 'Send via WhatsApp'}
            </Button>
            <Button
              size="small"
              variant="outlined"
              startIcon={<LinkIcon />}
              disabled={linkMutation.isPending}
              onClick={() => {
                setCopiedLink(null);
                linkMutation.mutate();
              }}
            >
              {linkMutation.isPending ? 'Generating…' : 'Copy portal link'}
            </Button>
          </Stack>

          {copiedLink && (
            <Alert severity="success">
              <Typography variant="body2" sx={{ mb: 1 }}>
                A new link was generated and copied to your clipboard. Any previously generated link no
                longer works.
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <TextField
                  value={copiedLink}
                  size="small"
                  fullWidth
                  InputProps={{ readOnly: true }}
                />
                <Tooltip title="Copy again">
                  <IconButton
                    size="small"
                    onClick={async () => {
                      await navigator.clipboard.writeText(copiedLink);
                    }}
                  >
                    <ContentCopyIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Stack>
            </Alert>
          )}

          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Invitation history
            </Typography>
            {historyQuery.isLoading && <CircularProgress size={20} />}
            {!historyQuery.isLoading && history.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                No invitations sent yet.
              </Typography>
            )}
            {history.length > 0 && (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Channel</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Sent by</TableCell>
                    <TableCell>Date/time</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((h) => (
                    <TableRow key={h.id}>
                      <TableCell>{CHANNEL_LABEL[h.channel]}</TableCell>
                      <TableCell>
                        <Chip size="small" label={h.status} color={STATUS_COLOR[h.status]} />
                        {h.errorMessage && (
                          <Typography variant="caption" color="text.secondary" display="block">
                            {h.errorMessage}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {h.sentBy.firstName} {h.sentBy.lastName}
                      </TableCell>
                      <TableCell>{new Date(h.sentAt).toLocaleString()}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};
